'use client';

import { useEffect, useRef } from 'react';
import { useExecutiveSummaryStore } from '@/lib/executiveSummaryStore';
import { snaplandToast } from '@/lib/snaplandToast';
import { useI18n } from '@/hooks/useI18n';
import { toast as baseToast } from '@/components/hooks/use-toast';
import { ToastAction } from '../toast';
import { usePdfViewer } from '@/hooks/usePdfViewer';
import { PdfViewer } from '../pdf-viewer';

const POLL_INTERVAL = 3000; // 3 seconds for background polling

/**
 * Background poller component that runs polling for all processing tasks
 * This component persists across page refreshes and works in the background
 */
export function ExecutiveSummaryBackgroundPoller() {
  const { t } = useI18n();
  const { tasks, updateTaskStatus, completeTask, failTask, cleanupOldTasks } = useExecutiveSummaryStore();
  const { isOpen, pdfUrl, title, openPdf, closePdf } = usePdfViewer();
  const pollingIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const notifiedTasksRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Cleanup old tasks on mount
    cleanupOldTasks();

    // Find all processing tasks and start polling for them
    const processingTasks = Object.values(tasks).filter(
      (task) => task.status === 'processing'
    );

    processingTasks.forEach((task) => {
      if (!pollingIntervalsRef.current.has(task.taskId)) {
        startPollingForTask(task.taskId);
      }
    });

    // Cleanup intervals on unmount
    return () => {
      pollingIntervalsRef.current.forEach((interval) => {
        clearInterval(interval);
      });
      pollingIntervalsRef.current.clear();
    };
  }, []);

  // Monitor tasks and start/stop polling as needed
  useEffect(() => {
    const processingTaskIds = Object.values(tasks)
      .filter((task) => task.status === 'processing')
      .map((task) => task.taskId);

    // Start polling for new processing tasks
    processingTaskIds.forEach((taskId) => {
      if (!pollingIntervalsRef.current.has(taskId)) {
        startPollingForTask(taskId);
      }
    });

    // Stop polling for tasks that are no longer processing
    const currentPollingTasks = Array.from(pollingIntervalsRef.current.keys());
    currentPollingTasks.forEach((taskId) => {
      if (!processingTaskIds.includes(taskId)) {
        stopPollingForTask(taskId);
      }
    });
  }, [tasks]);

  const startPollingForTask = (taskId: string) => {
    const pollTask = async () => {
      try {
        const response = await fetch(`/api/executive-summary/task-status/${taskId}`);

        if (!response.ok) {
          // Don't fail on network errors, just log and continue polling
          // console.log(`Failed to check status for task ${taskId}, will retry...`, response.status);
          return; // Continue polling
        }

        const statusData = await response.json();

        if (statusData.status === 'SUCCESS') {
          // Verify PDF is actually ready before marking as complete
          const pdfUrl = `/api/executive-summary/pdf/${taskId}`;
          
          // Update status to finalizing while we wait for PDF
          const currentTask = tasks[taskId];
          if (currentTask && currentTask.status === 'processing') {
            updateTaskStatus(taskId, 'finalizing');
          }
          
          // Check if result indicates PDF is ready
          if (statusData.result) {
            
            // Check if result has an error - fail on any error
            if (statusData.result.status === 'error') {
              // console.log(`[Background] ❌ Task ${taskId} failed with error: ${statusData.result.message}`);
              failTask(taskId, statusData.result.message || 'Task failed with error');
              stopPollingForTask(taskId);
              
              // Show error notification only once
              if (!notifiedTasksRef.current.has(taskId)) {
                notifiedTasksRef.current.add(taskId);
                snaplandToast.error(
                  t('executiveSummary.error'),
                  t('executiveSummary.generationFailed')
                );
              }
              return;
            }
          } else {
            // console.log(`[Background] ⚠️ Task ${taskId} has no result yet`);
          }
          
          // If statusData indicates PDF is ready, skip verification
          if (statusData.pdfReady || (statusData.result?.status === 'success' && statusData.result?.pdf_path)) {
            completeTask(taskId, pdfUrl);
            stopPollingForTask(taskId);

            // Show notification only once
            if (!notifiedTasksRef.current.has(taskId)) {
              notifiedTasksRef.current.add(taskId);
              baseToast({
                title: t('executiveSummary.summaryReady'),
                description: t('executiveSummary.summaryReadyDescription'),
                variant: 'success',
                duration: 10000,
                action: (
                  <ToastAction
                    altText={t('executiveSummary.openPdf')}
                    onClick={() => {
                      // Create rich title for the PDF
                      const currentTask = tasks[taskId];
                      let richTitle = '';
                      if (currentTask?.fulladdress && currentTask?.cityName) {
                        richTitle = `${currentTask.fulladdress} • ${currentTask.cityName} • RC ${currentTask.rcNumber}`;
                      } else if (currentTask?.cityName) {
                        richTitle = `${currentTask.cityName} • RC ${currentTask.rcNumber}`;
                      } else {
                        richTitle = `RC ${currentTask?.rcNumber || taskId}`;
                      }
                      openPdf(pdfUrl, richTitle);
                    }}
                    className="text-sm font-medium"
                  >
                    {t('executiveSummary.openPdf')}
                  </ToastAction>
                ),
              });
            }
            return;
          }
          
          try {
            const verifyResponse = await fetch(`/api/executive-summary/pdf/${taskId}`);
            
            if (!verifyResponse.ok) {
              const errorData = await verifyResponse.json().catch(() => ({}));
              return; // Continue polling
            }
            
          } catch (error) {
            // console.log(`[Background] ❌ Error verifying PDF for task ${taskId}:`, error);
            return; // Continue polling
          }

          completeTask(taskId, pdfUrl);
          stopPollingForTask(taskId);

          // Show notification only once
          if (!notifiedTasksRef.current.has(taskId)) {
            notifiedTasksRef.current.add(taskId);
            baseToast({
              title: t('executiveSummary.summaryReady'),
              description: t('executiveSummary.summaryReadyDescription'),
              variant: 'success',
              duration: 10000, // Show for 10 seconds
              action: (
                <ToastAction
                  altText={t('executiveSummary.openPdf')}
                  onClick={() => {
                    // Create rich title for the PDF
                    const currentTask = tasks[taskId];
                    let richTitle = '';
                    if (currentTask?.fulladdress && currentTask?.cityName) {
                      richTitle = `${currentTask.fulladdress} • ${currentTask.cityName} • RC ${currentTask.rcNumber}`;
                    } else if (currentTask?.cityName) {
                      richTitle = `${currentTask.cityName} • RC ${currentTask.rcNumber}`;
                    } else {
                      richTitle = `RC ${currentTask?.rcNumber || taskId}`;
                    }
                    openPdf(pdfUrl, richTitle);
                  }}
                  className="text-sm font-medium"
                >
                  {t('executiveSummary.openPdf')}
                </ToastAction>
              ),
            });
          }
        } else if (statusData.status === 'FAILURE') {
          // console.log(`[Background] ❌ Task ${taskId} FAILED`);
        } else {
          // console.log(`[Background] ⏳ Task ${taskId} status: ${statusData.status} - continuing to poll...`);
        }
        
        if (statusData.status === 'FAILURE') {
          failTask(taskId, 'Task failed to process');
          stopPollingForTask(taskId);

          // Show error notification only once
          if (!notifiedTasksRef.current.has(taskId)) {
            notifiedTasksRef.current.add(taskId);
            snaplandToast.error(
              t('executiveSummary.error'),
              t('executiveSummary.generationFailed')
            );
          }
        }
      } catch (error) {
        // Network error or other fetch error - don't fail, just log and retry
        // console.log(`Network error while polling task ${taskId}, will retry...`, error);
        // Continue polling - don't stop or fail the task
      }
    };

    // Poll immediately and then at intervals
    pollTask();
    const interval = setInterval(pollTask, POLL_INTERVAL);
    pollingIntervalsRef.current.set(taskId, interval);
  };

  const stopPollingForTask = (taskId: string) => {
    const interval = pollingIntervalsRef.current.get(taskId);
    if (interval) {
      clearInterval(interval);
      pollingIntervalsRef.current.delete(taskId);
    }
  };

  return (
    <>
      <PdfViewer
        isOpen={isOpen}
        onClose={closePdf}
        pdfUrl={pdfUrl || ''}
        title={title || 'PDF Document'}
      />
    </>
  );
}

